import { assign } from '../../utilities/objects';

const DEFAULT_PAGE_DURATION = 10 * 60 * 1000; // 10 minutes
const DEFAULT_MAX_AGE = 24 * 60 * 60 * 1000; // For a total of 1 day.

/**
 * Mapping of demands on items.
 */
export interface IItemDemands {
  /**
   * Maps item keys to truthy values indicating a demand on the item.
   * The value may be anything truthy.
   */
  [itemKey: string]: {};
}

/**
 * Options used to construct an `ItemPreserver`.
 */
export interface IItemPreserverOptions {
  pageDuration?: number;
  maxAge?: number;
}

/**
 * Model component which tracks a set of items which must be preserved during
 * cache-pruning operations.
 *
 * The set of 'preserved items' has two parts:
 * 1. A mapping of active 'owner' components and their current cache demands.
 * 2. A historical record of item last-accesses which slowly expires.
 *
 * Components rendered by the UX should use `registerDemands` and `removeDemands` to
 * ensure that their dependencies are not removed from the cache during a prune operation.
 * Components should also record their usage of items as 'last accesses' so that the
 * cache may continue to be used to reduce data fetches.
 *
 * Unlike most store-related components, this component keeps mutable state
 * to avoid costly iteration of object keys and values, and to avoid store churn.
 *
 * Current demands for items are tracked in a `Map`, per-owner. An owner may update
 * its demand list at any time, and finally remove it when disposed.
 *
 * The last-accessed items are tracked in a list of pages, whert each page represents
 * item accessed within a time window. Whenever the configured time window elapses,
 * subsequent last-accesses are recorded into a new page, and any pages representing
 * times beyond an age limit are removed.
 *
 * The set of last-accessed items may be trimmed to a time window, or removed entirely,
 * which gives the caller options for dealing with memory usage.
 *
 * The overall set of items to preserve may be requested at any time.
 */
export class ItemPreserver {
  /**
   * A mapping of owners to their current in-demand objects.
   */
  private _currentDemands: Map<object, IItemDemands>;

  /**
   * Pages representing the last-accessed within a time window.
   * Higher indices represent older pages, and are the first to be trimmed off.
   */
  private _lastAccessPages: IItemDemands[];

  /**
   * The time window a 'page' represents.
   */
  private _pageDuration: number;
  /**
   * The maximum number of pages of last-accessed items to preserve.
   */
  private _maxPages: number;

  /**
   * The time at which this item preserver was last written.
   */
  private _lastWriteTime: number;

  /**
   * Constructs a new item preserver.
   * @param time - The current time. Used as the initial 'write time'.
   * @param options - Overrides the default page duration and max age.
   */
  constructor(time: number, options: IItemPreserverOptions = {}) {
    const {
      pageDuration = DEFAULT_PAGE_DURATION,
      maxAge = DEFAULT_MAX_AGE
    } = options;

    this._pageDuration = pageDuration;
    this._maxPages = Math.ceil(maxAge / pageDuration);
    this._lastWriteTime = time;

    this._currentDemands = new Map();

    this._lastAccessPages = [];
  }

  /**
   * Registers item demands for a specific owner.
   * @param owner - An object which 'owns' the demands. Required to remove demands.
   * @param dependencies - A mapping of items to truthy values, indicating that the items are in-demand.
   */
  public registerDemands(owner: object, dependencies: IItemDemands): void {
    this._currentDemands.set(owner, dependencies);
  }

  /**
   * Removes item demands for a specific owner.
   * @param owner - An object which 'owns' the demands.
   */
  public removeDemands(owner: object): void {
    this._currentDemands.delete(owner);
  }

  /**
   * Records that items have been accessed and indicates the current time.
   * @param items - The set of items which have been recently-accessed.
   * @param time - The new current time. The preserver is internally updated to reflect the passage.
   */
  public recordLastAccesses(items: IItemDemands, time: number): void {
    this._fillPages(time);

    this._lastAccessPages.splice(this._maxPages, Infinity);

    assign(this._lastAccessPages[0], items);

    this._lastWriteTime = time;
  }

  /**
   * Trims the record of last-accesses to a specified maximum age.
   * @param time - The current time. The state is updated to reflect the passage.
   * @param maxAge - The maximm age to preserve in the history.
   */
  public trimLastAccesses(time: number, maxAge: number = this._pageDuration * this._maxPages): void {
    this._fillPages(time);

    this._lastAccessPages.splice(Math.min(this._maxPages, Math.floor(maxAge / this._pageDuration)), Infinity);

    this._lastWriteTime = time;
  }

  /**
   * Gets the aggregate set of item dependencies, based on current demands and last-access history.
   * This gets everything regardless of the current time. To keep things up-to-date, use `trimLastAccesses` first.
   */
  public getPreservedItems(): IItemDemands {
    const preservedItems: IItemDemands = {};

    this._currentDemands.forEach((demands: IItemDemands) => {
      assign(preservedItems, demands);
    });

    for (const page of this._lastAccessPages) {
      assign(preservedItems, page);
    }

    return preservedItems;
  }

  private _fillPages(time: number): void {
    let duration = time - this._lastWriteTime;

    while (duration >= this._pageDuration || this._lastAccessPages.length === 0) {
      this._lastAccessPages.unshift({});

      duration -= this._pageDuration;
    }
  }
}
